/* -------------------------------------------------------------------------- */
/* --------------------------- THEME : FRAMEWORKS --------------------------- */

// FRAMEWORKS : NODES_MODULES
import gsap from "gsap";
import "gsap/EasePack";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import ScrollSmoother from "gsap/ScrollSmoother";
gsap.registerPlugin(ScrollSmoother, ScrollTrigger, SplitText);

import Swiper from "swiper";
import {
	// Autoplay,
	// Thumbs,
	// EffectFade,
	// Navigation,
	// Pagination,
	Scrollbar,
} from "swiper/modules";

/* -------------------------------------------------------------------------- */
/* ------------------------ THEME : GLOBAL VARIABLES ------------------------ */

// Default : Smooth scrolling
let scrollSmooth = false;

/* -------------------------------------------------------------------------- */
/* ------------------------- THEME : GLOBAL EVENTS -------------------------- */

// DOCUMENT READY :
document.addEventListener("DOMContentLoaded", () => {
	// remove 'page-no-js' body class
	document.body.classList.remove("page-no-js");
	gsapPreloader();
	scrollDetection(160);
	menuBurger();
	menuDropdown();
	bannerInit();
	bannerToMarquee();
	dataSrc();
	dataClone();
	dataPopin();
	dataToggle();
});

// DOCUMENT RESIZED (avec ScrollTrigger)
ScrollTrigger.addEventListener("refreshInit", () => {
	gsapScrollSmootherRefresh();
	bannerToMarquee();
});

/* -------------------------------------------------------------------------- */
/* ----------------------- THEME : GLOBAL FUNCTIONS ------------------------- */

// Activation/désactivation du scroll
function scrollEnabled(bool) {
	if (bool === false || bool === true) {
		const enableScroll = bool === true;

		if (scrollSmooth) {
			scrollSmooth.paused(!enableScroll);
		}

		document.documentElement.style.overflowY = enableScroll ? "auto" : "hidden";
	}
}

// Scroll vers une ancre
const scrollToAnchor = (scrollOffset = 100) => {
	// Fonction pour gérer le défilement
	const scrollToElement = (target) => {
		const element = document.querySelector(target);
		if (element) {
			if (scrollSmooth) {
				// Avec ScrollSmoother
				scrollSmooth.scrollTo(target, true, `top ${scrollOffset}px`);
			} else {
				// Par défaut
				window.scrollTo({
					top:
						element.getBoundingClientRect().top + window.scrollY - scrollOffset,
					behavior: "smooth",
				});
			}
		}
	};

	// au chargement
	const hashInUrl = window.location.hash;
	if (hashInUrl) {
		scrollToElement(hashInUrl);
	}

	// avec un [href="#..."]
	const linksWithAnchor = document.querySelectorAll('a[href^="#"]');
	linksWithAnchor.forEach((link) => {
		link.addEventListener("click", (event) => {
			const anchorHref = link.getAttribute("href");
			if (anchorHref !== "#") {
				event.preventDefault();
				scrollToElement(anchorHref);
			}
		});
	});

	// avec un [data-scrollto]
	const dataWithScrollTo = document.querySelectorAll("[data-scrollto]");
	dataWithScrollTo.forEach((data) => {
		data.addEventListener("click", () => {
			let anchorData = data.getAttribute("data-scrollto");
			if (anchorData === "auto") {
				// Calculer la position de défilement pour 'auto'
				const autoOffset = window.innerHeight + window.scrollY - scrollOffset;
				window.scrollTo({
					top: autoOffset,
					behavior: "smooth",
				});
			} else {
				// Utiliser la valeur de data-scrollto comme cible pour scrollToElement
				scrollToElement(anchorData);
			}
		});
	});
};

// Détection du scroll sur le body
const scrollDetection = (scrollOffset = 1) => {
	let position = window.scrollY;

	if (position > scrollOffset) {
		document.body.classList.add("page-scrolled");
	} else {
		document.body.classList.remove("page-scrolled");
	}

	document.addEventListener("scroll", () => {
		// Détecter la direction du scroll
		let scroll = window.scrollY;
		// Scroll vers le bas
		if (scroll > position) {
			if (scroll < scrollOffset) {
				document.body.classList.remove("page-scrolled");
				document.body.classList.remove("page-scrolling");
			} else {
				document.body.classList.add("page-scrolling");
				document.body.classList.add("page-scrolled");
			}
		}
		// Scroll vers le haut
		else {
			document.body.classList.remove("page-scrolling");
			if (scroll < scrollOffset) {
				document.body.classList.remove("page-scrolled");
			}
		}
		// Scroll max ?
		if (scroll + window.innerHeight >= document.documentElement.scrollHeight) {
			document.body.classList.add("page-scroll-completed");
		} else {
			document.body.classList.remove("page-scroll-completed");
		}
		position = scroll;
	});
};

// Gestion du menu burger
const menuBurger = () => {
	const headerBurger = document.querySelector("#page-header-burger");
	const headerBackdrop = document.querySelector("#page-header-backdrop");

	if (headerBurger) {
		headerBurger.addEventListener("click", () => {
			if (document.body.classList.contains("page-with-fullscreenmenu")) {
				scrollEnabled(true);
				document.body.classList.remove("page-with-fullscreenmenu");
			} else {
				scrollEnabled(false);
				document.body.classList.add("page-with-fullscreenmenu");
				gsapMenuBurger();
			}
		});
	}

	if (headerBackdrop) {
		headerBackdrop.addEventListener("click", () => {
			if (document.body.classList.contains("page-with-fullscreenmenu")) {
				scrollEnabled(true);
				document.body.classList.remove("page-with-fullscreenmenu");
			} else {
				scrollEnabled(false);
				document.body.classList.add("page-with-fullscreenmenu");
			}
		});
	}
};

// Gestion des dropdowns
const menuDropdown = () => {
	const menuItemLinks = document.querySelectorAll(
		'.menu .menu__item .menu__link[href="#"]'
	);

	menuItemLinks.forEach((item) => {
		item.closest(".menu__item").classList.add("menu__item--parent");
		item.addEventListener("click", (event) => {
			event.preventDefault();
			item.closest(".menu__item").classList.toggle("menu__item--active");
		});
	});

	document.addEventListener("mouseup", (event) => {
		const activeMenuItems = document.querySelectorAll(
			".menu .menu__item.menu__item--active"
		);

		activeMenuItems.forEach((activeItem) => {
			if (
				!activeItem.isEqualNode(event.target) &&
				!activeItem.contains(event.target)
			) {
				activeItem.classList.remove("menu__item--active");
			}
		});
	});
};

// Activation/désactivation de la bannière
const bannerInit = () => {
	// Vérifie si la bannière a déjà été fermée durant cette session
	if (sessionStorage.getItem("bannerClosed") === "true") {
		return;
	}

	const pageBanner = document.querySelector("#page-banner");
	const pageBannerClose = document.querySelector("#page-banner-close");

	if (pageBanner) {
		// actualisation
		document.body.classList.add("page-with-banner");
		// bouton de fermeture
		pageBannerClose.addEventListener("click", () => {
			document.body.classList.remove("page-with-banner");

			// Enregistre en session que la bannière a été fermée
			sessionStorage.setItem("bannerClosed", "true");
		});
	}
};

// Gestion du défilement horizontal de la bannière
const bannerToMarquee = () => {
	const pageBanner = document.querySelector("#page-banner");

	if (pageBanner) {
		const bannerwrapperWidth = pageBanner.offsetWidth;
		const bannerTextWidth =
			pageBanner.querySelector(".banner__text").offsetWidth;
		if (bannerwrapperWidth > bannerTextWidth + 60) {
			pageBanner.classList.remove("page__banner--scrolling");
		} else {
			pageBanner.classList.add("page__banner--scrolling");
		}
	}
};

// Convertir les [data-src] en [src]
const dataSrc = () => {
	// Select all elements with a data-src attribute
	const srcElements = document.querySelectorAll("[data-src]");
	srcElements.forEach((element) => {
		element.setAttribute("src", element.getAttribute("data-src"));
		element.removeAttribute("data-src");
	});
};

// Cloner les éléménts [data-clone]
const dataClone = () => {
	const cloneTargets = document.querySelectorAll("[data-clone]");

	cloneTargets.forEach((cloneOriginal) => {
		const cloneValue = cloneOriginal.getAttribute("data-clone");
		const cloneParent = cloneOriginal.parentNode;

		if (!cloneParent) return;
		let cloneCount;

		if (cloneValue === "auto" || !cloneValue || cloneValue === undefined) {
			const cloneWidth = cloneOriginal.offsetWidth;
			cloneCount = Math.ceil(window.innerWidth / cloneWidth) + 1;
			// console.log("cloneCount: " + cloneCount);
		} else {
			const parsedCount = parseInt(cloneValue, 10);
			if (isNaN(parsedCount) || parsedCount <= 0) return;
			cloneCount = parsedCount;
		}

		for (let i = 0; i < cloneCount; i++) {
			const clone = cloneOriginal.cloneNode(true);
			clone.removeAttribute("data-clone");
			clone.setAttribute("data-clone-child", `${i + 1}/${cloneCount}`);
			cloneParent.insertBefore(clone, cloneOriginal);
		}

		cloneOriginal.remove(); // Supprimer l'élément initial après clonage
	});
};

// Gestions des actions des [data-popin]
const dataPopin = () => {
	const popins = document.querySelectorAll("[data-popin]");

	popins.forEach((popin) => {
		popin.addEventListener("click", (event) => {
			// event.preventDefault();

			// ouvrir/fermer la popin
			const thispopinName = popin.dataset.popin;
			if (thispopinName) {
				const thispopinEl = document.querySelector(
					`.page__popin#${thispopinName}`
				);
				thispopinEl.classList.toggle("page__popin--open");
			}

			// mettre les vidéos des popins en pause
			const htmlVideoFrompopin =
				document.querySelectorAll(".page__popin video");
			if (htmlVideoFrompopin.length > 0) {
				htmlVideoFrompopin.forEach((video) => {
					video.pause();
				});
			}
		});
	});
};

// Gestions des actions des [data-toggle]
const dataToggle = () => {
	// Récupération de tous les écouteurs
	const toggleListeners = document.querySelectorAll("[data-toggle]");

	// Écouteur d'événement pour chaque écouteur
	toggleListeners.forEach((togglelistener) => {
		const toggleTargetSelector = togglelistener.dataset.toggle;
		const toggleTarget = toggleTargetSelector
			? document.querySelector(toggleTargetSelector)
			: null;

		togglelistener.addEventListener("click", (event) => {
			if (toggleTarget) {
				// event.stopPropagation();
				toggleTarget.classList.toggle("--active");
			}
		});
	});

	// Gestionnaire pour les clics hors des éléments toggle
	document.addEventListener("click", (event) => {
		toggleListeners.forEach((togglelistener) => {
			const toggleTargetSelector = togglelistener.dataset.toggle;
			const toggleTarget = toggleTargetSelector
				? document.querySelector(toggleTargetSelector)
				: null;
			if (
				toggleTarget &&
				toggleTarget.classList.contains("--active") &&
				!toggleTarget.contains(event.target) &&
				!togglelistener.contains(event.target)
			) {
				toggleTarget.classList.remove("--active");
			}
		});
	});
};


// GSAP : Gestion des actions de [data-player]
const gsapDataPlayer = (breakpoint = 1024) => {
	const playerListeners = document.querySelectorAll("[data-player]");
	playerListeners.forEach((playerListener) => {
		playerListener.addEventListener("click", () => {
			// Vérifie s'il y a déjà un player actif
			if (document.querySelector(".page__player")) return;

			// On bloque le scroll
			scrollEnabled(false);

			// Création du conteneur du player
			const player = document.createElement("div");
			player.className = "page__player";
			document.querySelector("#page-wrapper").append(player);
			gsap.to(player, {
				duration: 0.3,
				ease: "power1.out",
				opacity: 1,
				visibility: "visible",
			});

			// Création de la vidéo
			const video = document.createElement("video");
			video.className = "player__video";
			video.src = playerListener.dataset.player;
			player.append(video);

			// Bouton de fermeture
			const buttonClose = document.createElement("a");
			buttonClose.className = "player__close";
			player.append(buttonClose);

			// Curseur personnalisé
			const cursor = document.createElement("div");
			cursor.className = "player__cursor";
			player.append(cursor);
			// Animation d'entrée du bouton de fermeture
			gsap.to(".player__close", {
				duration: 0.3,
				scale: 1,
				opacity: 1,
			});

			// Déplacement dynamique du bouton de fermeture
			const moveButtonClose = (e) => {
				gsap.to(".player__close", {
					x: e.clientX,
					y: e.clientY,
					duration: 0.3,
				});
				gsap.to(".player__cursor", {
					x: e.clientX,
					y: e.clientY,
					duration: 0.3,
				});
			};
			player.addEventListener("mousemove", moveButtonClose);

			// Fonction de fermeture du player
			const closePlayer = () => {
				// On débloque le scroll
				scrollEnabled(true);
				gsap.to(player, {
					duration: 0.3,
					ease: "power1.out",
					opacity: 0,
					onComplete: () => player.remove(),
				});
			};

			// Lecture automatique de la vidéo
			video.play();
			video.addEventListener("ended", closePlayer);
			video.addEventListener("click", closePlayer);
			buttonClose.addEventListener("click", closePlayer);

			// Mode plein écran sur mobile
			if (window.innerWidth < breakpoint) {
				if (video.requestFullscreen) video.requestFullscreen();
				else if (video.webkitRequestFullscreen) video.webkitRequestFullscreen();
				else if (video.msRequestFullScreen) video.msRequestFullScreen();
			}

			// Ajout des contrôles personnalisés
			const addControls = () => {
				const playerControls = document.createElement("div");
				playerControls.className = "player__controls";
				player.append(playerControls);

				// Gestion de l'affichage du curseur et du bouton de fermeture
				playerControls.addEventListener("mouseenter", () => {
					gsap.to(buttonClose, { scale: 0 });
					gsap.to(cursor, { scale: 1 });
				});
				playerControls.addEventListener("mouseleave", () => {
					gsap.to(buttonClose, { scale: 1 });
					gsap.to(cursor, { scale: 0 });
				});

				// Bouton play/pause
				const playButton = document.createElement("div");
				playButton.className = "player__controls__play";
				playButton.innerHTML = "❚❚";
				playerControls.append(playButton);

				playButton.addEventListener("click", () => {
					if (video.paused) {
						video.play();
					} else {
						video.pause();
					}
					playButton.innerHTML = video.paused ? "►" : "❚❚";
				});

				// Barre de progression
				const progress = document.createElement("div");
				progress.className = "player__controls__progress";
				const progressBar = document.createElement("span");
				progress.append(progressBar);
				playerControls.append(progress);

				video.addEventListener("timeupdate", () => {
					gsap.to(progressBar, {
						width: (video.currentTime / video.duration) * 100 + "%",
					});
				});

				// Bouton de mute
				const muteButton = document.createElement("div");
				muteButton.className = "player__controls__mute";
				muteButton.innerHTML = "Mute";
				playerControls.append(muteButton);

				muteButton.addEventListener("click", () => {
					video.muted = !video.muted;
					muteButton.innerHTML = video.muted ? "Unmute" : "Mute";
				});
			};
			addControls();
		});
	});
};

/* -------------------------------- (SWIPER) -------------------------------- */

// Swiper : Initialisation des sliders
const swiperInit = () => {
	if (
		document.querySelectorAll("#autres-articles .swiper .swiper-slide").length >
		1
	) {
		new Swiper("#autres-articles .swiper", {
			// Core parameters
			slidesPerView: "auto",
			speed: 1200,
			resizeObserver: true,
			// Modules
			modules: [Scrollbar],
			scrollbar: {
				el: "#autres-articles .swiper-scrollbar",
				draggable: true,
			},
		});
	}
};

/* --------------------------------- (GSAP) --------------------------------- */

// GSAP : Initialisation du scrollSmoother (avec ScrollSmoother)
const initScrollSmooth = () => {
	scrollSmooth = ScrollSmoother.create({
		wrapper: "#page-wrapper",
		content: "#page-container",
		smooth: 1,
		smoothTouch: false,
		effects: true,
		normalizeScroll: false,
		ignoreMobileResize: true,
	});
};

// GSAP : Rafraîchissement du ScrollSmoother (avec ScrollSmoother)
const gsapScrollSmootherRefresh = () => {
	if (scrollSmooth) {
		// Désactiver sur Élémentor
		if (document.querySelector("body.elementor-editor-active")) {
			scrollSmooth.kill();
		} else {
			scrollSmooth.refresh();
		}
	}
};

// GSAP : Animations liés au menuBurger
const gsapMenuBurger = () => {
	const burgerTimeline = gsap.timeline();
	burgerTimeline.from(["#page-header .header__fullmenu .menu__item"], {
		duration: 0.6,
		stagger: 0.05,
		ease: "power1.out",
		autoAlpha: 0,
		x: 40,
	});
};

// GSAP : Animations au scroll (avec ScrollTrigger)
const gsapDataAnimation = () => {
	// [data-animation] + .elementor-element
	const elementsToAnimated = document.querySelectorAll([
		"#page-container [data-animation]",
		"#page-container .elementor-heading-title",
		"#page-container .elementor-element:not([data-animation]) > .elementor-element",
	]);
	elementsToAnimated.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10)
			: 0;
		ScrollTrigger.create({
			trigger: _this,
			start: "top 100%",
			end: "bottom 0",
			onEnter: () => {
				setTimeout(() => {
					_this.classList.add("--animated");
				}, delay);
			},
			onLeaveBack: () => {
				_this.classList.remove("--animated");
			},
			// markers: true,
		});
	});

	// data-animation="children-to-top"
	const childrenToTop = document.querySelectorAll(
		"[data-animation='children-to-top']"
	);
	childrenToTop.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10) / 1000
			: 0;
		gsap.from(_this.children, {
			y: "50%",
			autoAlpha: 0,
			duration: 1.2,
			ease: "power4.out",
			delay: delay,
			stagger: 0.1,
		});
	});

	// data-animation="reveal-text"
	const textsToAnimated = document.querySelectorAll([
		// global
		'#page-container [data-animation="reveal-text"]',
		"#page-container .elementor-element:not([data-animation]) > .elementor-widget-container > .elementor-heading-title",
	]);
	textsToAnimated.forEach((_thisText) => {
		const delay = _thisText.getAttribute("data-animation-delay")
			? parseInt(_thisText.getAttribute("data-animation-delay"), 10) / 1000
			: 0;
		const split = SplitText.create(_thisText, {
			type: "lines, words",
			autoSplit: true,
			mask: "lines",
			onSplit(self) {
				gsap.from(self.words, {
					scrollTrigger: {
						trigger: _thisText,
						start: "top 88%",
						toggleActions: "play none none reverse",
						// markers: true,
					},
					y: "100%",
					autoAlpha: 0,
					delay: delay,
					duration: 0.8,
					ease: "power3.out",
					stagger: 0.05,
				});
			},
		});
	});

	// data-animation="reveal-text-reading"
	const textsToReading = document.querySelectorAll(
		'[data-animation="reveal-text-reading"]'
	);
	textsToReading.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10) / 1000
			: 0;

		const split = SplitText.create(_this, {
			type: "words, chars",
			onSplit(self) {
				gsap.fromTo(
					self.chars,
					{
						opacity: 0.1,
					},
					{
						opacity: 1,
						delay: delay,
						stagger: 0.4,
						ease: "power1.out",
						scrollTrigger: {
							trigger: _this,
							start: "top 88%",
							end: "top 22%",
							scrub: true,
							// markers: true
						},
					}
				);
			},
		});
	});

	// data-animation="counter"
	const elementsToCounter = document.querySelectorAll(
		'[data-animation="counter"]'
	);
	elementsToCounter.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10)
			: 0;
		ScrollTrigger.create({
			trigger: _this,
			start: "top 100%",
			end: "bottom 0%",
			// markers: true,
			onEnter: () => {
				setTimeout(() => {
					gsap.from(_this, {
						duration: 1.5,
						textContent: 0,
						snap: { textContent: 1 },
						stagger: {
							each: 1.0,
							onUpdate: function () {
								this.targets()[0].innerHTML = Math.ceil(
									this.targets()[0].textContent
								)
									// conserver les "."
									.toString()
									.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
									// conserver les espaces
									.toString()
									.replace(/\B(?=(\d{3})+(?!\d))/g, "&nbsp;");
							},
						},
					});
				}, delay);
			},
		});
	});

	// data-animation="skid"
	const elementsToskid = document.querySelectorAll([
		'body:not(.elementor-editor-active) [data-animation="skid"]',
	]);
	elementsToskid.forEach((_this) => {
		gsap.to(_this, {
			scrollTrigger: {
				trigger: _this,
				pin: true,
				pinSpacing: false,
				start: "top top",
				// markers: true,
			},
		});
	});

	/*
	// data-animation="draw"
	const elementsToDraw = document.querySelectorAll(['[data-animation="draw"]']);
	elementsToDraw.forEach((_this) => {
		const delay = _this.getAttribute("data-delay")
			? parseInt(_this.getAttribute("data-delay"), 10) / 100
			: 0;
		gsap.from(_this.querySelectorAll("g > *"), {
			drawSVG: 0,
			ease: "power1.inOut",
			duration: 1.2,
			delay: delay,
			scrollTrigger: {
				trigger: _this,
				start: "top 88%",
				// markers: true,
			},
		});
	});
	*/

	/*
	// data-animation="drawing"
	const elementsToDrawing = document.querySelectorAll([
		'[data-animation="drawing"]',
	]);
	elementsToDrawing.forEach((_this) => {
		const delay = _this.getAttribute("data-delay")
			? parseInt(_this.getAttribute("data-delay"), 10) / 100
			: 0;
		gsap.from(_this.querySelectorAll("g > *"), {
			drawSVG: 0,
			ease: "power1.inOut",
			duration: 1.8,
			delay: delay,
			stagger: 0.1,
			scrollTrigger: {
				trigger: _this,
				start: "top 88%",
				end: "bottom 44%",
				scrub: 1.1,
				// markers: true,
			},
		});
	});
	*/
};

// Actions exécutées après le preLoader d'accueil
const afterPreloader = () => {
	scrollEnabled(true);
	swiperInit();
	gsapDataAnimation();
	scrollToAnchor();
	gsapDataPlayer();
};

// GSAP : Gestion du preloader d'accueil
const gsapPreloader = () => {
	// Chargement des fonctions GSAP préalables
	initScrollSmooth();
	scrollEnabled(false);

	// Lancement de l'animation de chargement
	const loaderTimeline = gsap.timeline();
	loaderTimeline
		// animation
		.to("#page-preloader .preloader__wrapper", {
			delay: 0.3,
			duration: 0.6,
			ease: "power1.out",
			opacity: 0,
		})
		.set("#page-preloader", {
			visibility: "hidden",
		});

	// Functions succédentes
	loaderTimeline.add(afterPreloader, loaderTimeline.duration() - 0.3);
};
